import React, { useCallback, useEffect, useState } from 'react';
import {
    Checkbox,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TablePagination,
    TableRow,
} from '@material-ui/core';
import { EnhancedTableToolbar } from './EnhancedTableToolbar';
import { EnhancedTableHead, SortOrder } from './EnhancedTableHead';
import clsx from 'clsx';
import { invertSorter, sortByLocaleCompare, Sorter } from '../../util/sorting';

const useStyles = makeStyles(() => ({
    container: {
        overflowX: 'auto',
    },
    cardMargin: {
        margin: -16,
    },
}));

export type EnhancedTableCellData = React.ReactNode;

export interface SortableTableHeadDefinition<T extends EnhancedTableData> {
    label: EnhancedTableCellData;
    sortable?: boolean;
    sorter?: Sorter<T>;
}

export type EnhancedTableColumnDefinition<T extends EnhancedTableData> = SortableTableHeadDefinition<T>|EnhancedTableCellData;

export interface EnhancedTableData {
    id: string;
    columns: EnhancedTableCellData[];
    selectable?: boolean;
    checkboxLabel?: string;
    selected?: boolean;
}

export interface EnhancedTableProps<T extends EnhancedTableData> {
    columns: EnhancedTableColumnDefinition<T>[];
    data: T[];
    tableHeadActions?: (items: T[]) => React.ReactNode;
    selectable?: boolean;
    disabled?: boolean;
    placeholder?: string;
    isCardContent?: boolean;
    getSelectionStatusLabel?: (selectedItems: number, totalItems: number) => React.ReactNode;
    pagination?: boolean;
    itemsPerPage?: number;
    itemsPerPageOptions?: Array<number | { value: number, label: string }>;
    onSelectionChanged?: (selectedItems: T[]) => unknown;
}

const getItemsPerPage = (initialValue: number, options: Required<EnhancedTableProps<never>['itemsPerPageOptions']>): number => {
    const optionValues = options.map(option => typeof option === 'object' ? option.value ?? -1 : option);
    return optionValues.length === 0 ? initialValue : optionValues[0];
};

const EnhancedTable = <T extends EnhancedTableData>(props: EnhancedTableProps<T>): JSX.Element => {
    const classes = useStyles({});

    const [ selectedItems, setSelectedItems ] = useState<T[]>([]);

    const updateSelectedItems = useCallback<typeof setSelectedItems>((update) => {
        setSelectedItems(currentItems => {
            let nextItems: T[];
            if (typeof update === 'function') {
                nextItems = update(currentItems);
            } else {
                nextItems = update;
            }
            props.onSelectionChanged?.(nextItems);
            return nextItems;
        });
    }, [ props.onSelectionChanged ]);

    const [ sortByIndex, setSortByIndex ] = useState(0);
    const [ sortOrder, setSortOrder ] = useState(SortOrder.ASC);

    const {
        itemsPerPage: initialItemsPerPage = -1,
        itemsPerPageOptions = [ 5, 10, 20 ],
        pagination: usePagination = false,
    } = props;
    const [ currentPage, setCurrentPage ] = useState(0);
    const [ itemsPerPage, setItemsPerPage ] = useState(usePagination ? getItemsPerPage(initialItemsPerPage, itemsPerPageOptions) ?? -1 : -1);

    const isItemSelected = itemId => !!selectedItems.find(item => item.id === itemId);

    const toggleItemSelection = itemId => () => {
        if (isItemSelected(itemId)) {
            updateSelectedItems(items => items.filter(item => item.id !== itemId));
        } else {
            const newItem = props.data.find(row => row.id === itemId);
            if (newItem) {
                updateSelectedItems(items => [ ...items, newItem ]);
            }
        }
    };

    const getSortedItems = (data: T[]) => {
        let sorter: Sorter<T>;

        if (sortByIndex <= props.columns.length && Object.prototype.hasOwnProperty.call(props.columns[sortByIndex], 'sorter')) {
            sorter = (props.columns[sortByIndex] as SortableTableHeadDefinition<T>).sorter;
        } else {
            sorter = sortByLocaleCompare<T>(item => (item.columns[sortByIndex] ?? '').toString());
        }

        if (sortOrder === SortOrder.DESC) {
            sorter = invertSorter(sorter);
        }

        return data.sort(sorter);
    };

    const sortedItems = sortByIndex <= props.columns.length && (props.columns[sortByIndex] as SortableTableHeadDefinition<T>).sortable === true ? getSortedItems(props.data) : props.data;
    const activeItems = itemsPerPage > 0 ? sortedItems.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage) : sortedItems;

    // cleanup selection once data changes
    useEffect(() => {
        updateSelectedItems(selectedItems => selectedItems.filter(item => activeItems.find(input => input.id === item.id)));
    }, [ props.data, currentPage, updateSelectedItems ]);

    const selectableItems = activeItems.filter(row => row.selectable);

    const selectAll = () => updateSelectedItems(selectableItems);

    const selectNone = useCallback(() => updateSelectedItems([]), [ updateSelectedItems ]);

    const allSelected = () => selectableItems.every(item => selectedItems.indexOf(item) >= 0);

    const handlePageChange = useCallback((ev: unknown, newPage: number) => {
        setCurrentPage(newPage);
    }, []);

    const handleItemsPerPageChange = useCallback((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setItemsPerPage(parseInt(event.currentTarget.value, 10));
    }, []);

    return (
        <div className={clsx(props.isCardContent && classes.cardMargin)}>
            {(
                <EnhancedTableToolbar
                    allSelected={allSelected()}
                    selectedItems={selectedItems}
                    getActions={props.tableHeadActions}
                    totalItemCount={activeItems.length}
                    isCardContent={props.isCardContent}
                    getSelectionStatusLabel={props.getSelectionStatusLabel}
                    disabled={props.disabled}
                />
            )}
            <div className={classes.container}>
                <Table>
                    <EnhancedTableHead
                        columns={props.columns}
                        selectable={props.selectable}
                        selectAll={selectAll}
                        selectNone={selectNone}
                        allSelected={allSelected()}
                        selectionCount={selectedItems.length}
                        sortByIndex={sortByIndex}
                        setSortByIndex={setSortByIndex}
                        sortOrder={sortOrder}
                        setSortOrder={setSortOrder}
                        disabled={props.disabled}
                    />
                    {usePagination && (
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    colSpan={props.columns.length + (props.selectable ? 1 : 0)}
                                    count={props.data.length}
                                    page={currentPage}
                                    rowsPerPage={itemsPerPage}
                                    rowsPerPageOptions={itemsPerPageOptions}
                                    onChangeRowsPerPage={handleItemsPerPageChange}
                                    onChangePage={handlePageChange}
                                />
                            </TableRow>
                        </TableFooter>
                    )}
                    <TableBody>
                        {activeItems.map(row => {
                            const itemId = row.id;
                            const isSelected = isItemSelected(itemId);
                            return (
                                <TableRow
                                    key={itemId}
                                >
                                    {props.selectable && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isSelected}
                                                inputProps={{
                                                    'aria-label': row.checkboxLabel,
                                                }}
                                                onChange={toggleItemSelection(itemId)}
                                                disabled={props.disabled || !row.selectable}
                                                />
                                        </TableCell>
                                    )}
                                    {row.columns.map((cell, i) => (
                                        <TableCell key={i}>{cell}</TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                        {props.data.length === 0 && props.placeholder && (
                            <TableRow>
                                <TableCell colSpan={props.columns.length + (props.selectable ? 1 : 0)}>{props.placeholder}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export { EnhancedTable };
